import { supabase } from './supabaseClient';
import { getNegocioId } from './negocioActual';

// Movimientos de dinero del negocio para la vista de Balance.
// Reusa el libro unificado movimientos_cuenta (ya trae tipo, monto con
// signo, cuenta y fecha). Une el nombre de la cuenta (forma de pago).
// `hasta` (opcional, exclusivo) permite ver un mes anterior completo.
export async function obtenerMovimientos({ desde, hasta } = {}) {
  const neg = getNegocioId();
  let query = supabase
    .from('movimientos_cuenta')
    .select('*, cuentas ( nombre, tipo )')
    .eq('negocio_id', neg)
    .order('creado_en', { ascending: false });
  if (desde) query = query.gte('creado_en', desde);
  if (hasta) query = query.lt('creado_en', hasta);
  const { data, error } = await query;
  if (error) throw error;
  const movs = data || [];

  // El canal y el cliente viven en ventas / ingresos_libres (no en el
  // movimiento). Los traemos y los pegamos a cada movimiento por su referencia.
  const refsVenta = movs.filter((m) => m.tipo === 'venta').map((m) => m.referencia).filter(Boolean);
  const refsIng = movs.filter((m) => m.tipo === 'ingreso_libre').map((m) => m.referencia).filter(Boolean);
  const canalPorRef = {};
  const clientePorRef = {}; // referencia -> cliente_id
  if (refsVenta.length) {
    const { data: vts } = await supabase
      .from('ventas').select('ticket_id, canal, cliente_id').eq('negocio_id', neg)
      .in('ticket_id', refsVenta);
    (vts || []).forEach((v) => {
      if (!v.ticket_id) return;
      if (v.canal) canalPorRef[String(v.ticket_id)] = v.canal;
      if (v.cliente_id) clientePorRef[String(v.ticket_id)] = v.cliente_id;
    });
  }
  if (refsIng.length) {
    const { data: ings } = await supabase
      .from('ingresos_libres').select('id, canal, cliente_id').eq('negocio_id', neg)
      .in('id', refsIng);
    (ings || []).forEach((i) => {
      if (i.canal) canalPorRef[String(i.id)] = i.canal;
      if (i.cliente_id) clientePorRef[String(i.id)] = i.cliente_id;
    });
  }
  // Abonos: la referencia es el id del abono → cuenta_por_cobrar → cliente y,
  // vía el ticket, el canal de la venta a crédito (para mostrarlo en el abono).
  const refsAbono = movs.filter((m) => m.tipo === 'abono').map((m) => m.referencia).filter(Boolean);
  if (refsAbono.length) {
    const { data: abs } = await supabase
      .from('abonos').select('id, cxc_id').eq('negocio_id', neg).in('id', refsAbono);
    const cxcIds = [...new Set((abs || []).map((a) => a.cxc_id).filter(Boolean))];
    const cxcInfo = {}; // cxc_id -> { cliente_id, ticket_id }
    if (cxcIds.length) {
      const { data: cxcs } = await supabase
        .from('cuentas_por_cobrar').select('id, cliente_id, ticket_id').eq('negocio_id', neg).in('id', cxcIds);
      (cxcs || []).forEach((c) => { cxcInfo[String(c.id)] = c; });
    }
    const ticketIds = [...new Set(Object.values(cxcInfo).map((c) => c.ticket_id).filter(Boolean))];
    const canalPorTicket = {};
    if (ticketIds.length) {
      const { data: vts2 } = await supabase
        .from('ventas').select('ticket_id, canal').eq('negocio_id', neg).in('ticket_id', ticketIds).not('canal', 'is', null);
      (vts2 || []).forEach((v) => { if (v.ticket_id && v.canal) canalPorTicket[String(v.ticket_id)] = v.canal; });
    }
    (abs || []).forEach((a) => {
      const cx = cxcInfo[String(a.cxc_id)];
      if (cx?.cliente_id) clientePorRef[String(a.id)] = cx.cliente_id;
      if (cx?.ticket_id && canalPorTicket[String(cx.ticket_id)]) canalPorRef[String(a.id)] = canalPorTicket[String(cx.ticket_id)];
    });
  }

  // Nombres de los clientes referenciados.
  const clienteIds = [...new Set(Object.values(clientePorRef))];
  const nombrePorCliente = {};
  if (clienteIds.length) {
    const { data: cls } = await supabase
      .from('clientes').select('id, nombre').eq('negocio_id', neg).in('id', clienteIds);
    (cls || []).forEach((c) => { nombrePorCliente[String(c.id)] = c.nombre; });
  }
  movs.forEach((m) => {
    m.canal = canalPorRef[String(m.referencia)] || null;
    const cid = clientePorRef[String(m.referencia)];
    m.cliente = cid ? (nombrePorCliente[String(cid)] || null) : null;
  });
  return movs;
}

// Compras (tickets) del período desde la tabla `ventas`. A diferencia de
// obtenerMovimientos, ESTO INCLUYE las ventas fiadas (a crédito), que no
// generan movimiento de dinero. Una compra = un ticket. Sirve para el sorteo:
// cada compra es un boleto, aunque la clienta haya comprado al crédito.
export async function obtenerComprasPeriodo({ desde, hasta } = {}) {
  const neg = getNegocioId();
  let q = supabase
    .from('ventas')
    .select('id, ticket_id, cliente_id, total, vendido_en, a_credito, canal, concepto, metodo_pago, nombre_producto')
    .eq('negocio_id', neg)
    .order('vendido_en', { ascending: true });
  if (desde) q = q.gte('vendido_en', desde);
  if (hasta) q = q.lt('vendido_en', hasta);
  const { data, error } = await q;
  if (error) throw error;
  const filas = data || [];

  // Agrupar renglones por ticket (o por id si es venta suelta sin ticket).
  const porTicket = new Map();
  filas.forEach((v) => {
    const key = String(v.ticket_id || v.id);
    const cur = porTicket.get(key) || {
      ticket: key, cliente_id: v.cliente_id, total: 0, fecha: v.vendido_en,
      aCredito: !!v.a_credito, canal: v.canal || null, concepto: v.concepto || null,
      metodo: v.metodo_pago || null, nProductos: 0, primerProducto: v.nombre_producto || null,
    };
    cur.total += Number(v.total || 0);
    cur.nProductos += 1;
    if (new Date(v.vendido_en) < new Date(cur.fecha)) cur.fecha = v.vendido_en;
    porTicket.set(key, cur);
  });

  // Nombres de las clientas.
  const ids = [...new Set(filas.map((v) => v.cliente_id).filter(Boolean))];
  const nombrePorCliente = {};
  if (ids.length) {
    const { data: cls } = await supabase
      .from('clientes').select('id, nombre').eq('negocio_id', neg).in('id', ids);
    (cls || []).forEach((c) => { nombrePorCliente[String(c.id)] = c.nombre; });
  }

  return [...porTicket.values()]
    .map((c) => ({ ...c, cliente: c.cliente_id ? (nombrePorCliente[String(c.cliente_id)] || null) : null }))
    .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));
}

// Elimina una venta (single o de carrito) con reverso atómico de stock y
// dinero. `referencia` es el campo del movimiento (id de venta o ticket_id).
export async function eliminarVenta(referencia) {
  const { error } = await supabase.rpc('eliminar_venta', {
    p_negocio_id: getNegocioId(),
    p_referencia: String(referencia),
  });
  if (error) throw error;
}

// Renglones (productos) de una venta, para editarla.
export async function obtenerRenglonesVenta(referencia) {
  const { data, error } = await supabase
    .from('ventas')
    .select('*')
    .eq('negocio_id', getNegocioId())
    .or(`id.eq.${referencia},ticket_id.eq.${referencia}`);
  if (error) throw error;
  return data || [];
}

// Envío cobrado en una venta pagada (ingreso 'Envío nacional' ligado al ticket),
// para precargarlo al editar. Suma la columna envio (o el monto en registros viejos).
export async function obtenerEnvioVenta(referencia) {
  const { data, error } = await supabase
    .from('ingresos_libres')
    .select('envio, monto')
    .eq('negocio_id', getNegocioId())
    .eq('ticket_venta', String(referencia));
  if (error) throw error;
  return (data || []).reduce(
    (s, r) => s + (Number(r.envio || 0) > 0 ? Number(r.envio) : Number(r.monto || 0)),
    0
  );
}

// Edita una venta: revierte la original y crea la corregida (atómico).
// aCredito permite cambiar entre venta pagada y venta a crédito (fiado).
export async function editarVenta(referencia, { items, clienteId, metodoPago, cuentaId, descuento, concepto, nota, canal, aCredito = false, envio = 0, fecha = null }) {
  const { data, error } = await supabase.rpc('editar_venta', {
    p_negocio_id: getNegocioId(),
    p_referencia: String(referencia),
    p_items: items.map((i) => ({ producto_id: i.producto_id, cantidad: i.cantidad })),
    p_cliente_id: clienteId || null,
    p_metodo_pago: metodoPago || null,
    p_cuenta_id: cuentaId || null,
    p_descuento: parseFloat(descuento) || 0,
    p_concepto: concepto?.trim() || null,
    p_nota: nota?.trim() || null,
    p_canal: canal || null,
    p_a_credito: !!aCredito,
    p_envio: parseFloat(envio) || 0,
    p_fecha: fecha || null,
  });
  if (error) throw error;
  return data;
}

// Productos vendidos del período, uno por renglón (pagados y fiados), con la
// clienta y el estado del cobro de las ventas a crédito. Más reciente primero.
export async function obtenerProductosVendidos({ desde, hasta } = {}) {
  const neg = getNegocioId();
  let q = supabase
    .from('ventas')
    .select('id, ticket_id, producto_id, codigo_producto, nombre_producto, cantidad, precio_unitario, total, costo_unitario, cliente_id, vendido_en, a_credito, canal, metodo_pago, concepto, nota')
    .eq('negocio_id', neg)
    .order('vendido_en', { ascending: false });
  if (desde) q = q.gte('vendido_en', desde);
  if (hasta) q = q.lt('vendido_en', hasta);
  const { data, error } = await q;
  if (error) throw error;
  const filas = data || [];

  const idsCliente = [...new Set(filas.map((v) => v.cliente_id).filter(Boolean))];
  const clientes = {};
  if (idsCliente.length) {
    const { data: cls } = await supabase
      .from('clientes').select('id, nombre, telefono').eq('negocio_id', neg).in('id', idsCliente);
    (cls || []).forEach((c) => { clientes[String(c.id)] = c; });
  }

  // Cuenta por cobrar de cada venta fiada (para saber si ya pagó y cuánto debe).
  const tickets = [...new Set(filas.filter((v) => v.a_credito && v.ticket_id).map((v) => String(v.ticket_id)))];
  const cxcPorTicket = {};
  if (tickets.length) {
    const { data: cxcs } = await supabase
      .from('cuentas_por_cobrar').select('*').eq('negocio_id', neg).in('ticket_id', tickets);
    (cxcs || []).forEach((c) => { cxcPorTicket[String(c.ticket_id)] = c; });
  }

  return filas.map((v) => ({
    ...v,
    referencia: String(v.ticket_id || v.id),
    cliente: v.cliente_id ? (clientes[String(v.cliente_id)] || null) : null,
    cxc: v.a_credito && v.ticket_id ? (cxcPorTicket[String(v.ticket_id)] || null) : null,
  }));
}
